import { useEffect, useMemo, useState } from 'react';

import { getSpaceConversationsFetchAction } from '../../services/useGetSpaceConversations/useGetSpaceConversations.fetch';
import type { RovoNavigationConversation } from '../../types/rovo-navigation-conversation';

const SEARCH_SPACE_CONVERSATION_COUNT = 50;

type SearchSpaceConversationsBySpaceId = Map<string, RovoNavigationConversation[]>;

type UseSearchSpaceConversationsProps = {
	cloudId: string;
	product: string;
	experienceId: string;
	spaceIds: string[];
	enabled: boolean;
};

type UseSearchSpaceConversationsResult = {
	conversationsBySpaceId: SearchSpaceConversationsBySpaceId;
	isLoading: boolean;
};

export const useSearchSpaceConversations = ({
	cloudId,
	product,
	experienceId,
	spaceIds,
	enabled,
}: UseSearchSpaceConversationsProps): UseSearchSpaceConversationsResult => {
	const [conversationsBySpaceId, setConversationsBySpaceId] =
		useState<SearchSpaceConversationsBySpaceId>(() => new Map());
	const [isLoading, setIsLoading] = useState(false);
	const spaceIdsKey = spaceIds.join('\u0000');
	const stableSpaceIds = useMemo(
		() => (spaceIdsKey ? spaceIdsKey.split('\u0000') : []),
		[spaceIdsKey],
	);

	useEffect(() => {
		if (!enabled || stableSpaceIds.length === 0) {
			setConversationsBySpaceId(new Map());
			setIsLoading(false);
			return;
		}

		let isCancelled = false;
		setIsLoading(true);

		void Promise.allSettled(
			stableSpaceIds.map(async (spaceId) => {
				const conversationPage = await getSpaceConversationsFetchAction({
					spaceId,
					cloudId,
					product,
					experienceId,
					count: SEARCH_SPACE_CONVERSATION_COUNT,
				});

				return [spaceId, conversationPage.conversations] as const;
			}),
		)
			.then((results) => {
				if (isCancelled) {
					return;
				}

				const fulfilledEntries = results.flatMap((result) =>
					result.status === 'fulfilled' ? [result.value] : [],
				);

				setConversationsBySpaceId(new Map(fulfilledEntries));
			})
			.finally(() => {
				if (!isCancelled) {
					setIsLoading(false);
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [cloudId, enabled, experienceId, product, stableSpaceIds]);

	return {
		conversationsBySpaceId,
		isLoading,
	};
};
